import { 
  Box, Paper, Typography, TextField, Button, Alert, 
  Container, CircularProgress, Divider
} from '@mui/material';
import { Microsoft } from '@mui/icons-material';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import AuthService from '../services/AuthService';
import VersionService from '../services/VersionService';
import { useSystemPublic } from '../hooks/api/config';
import { usePublicAuthProviders } from '../hooks/api/useAuthProviders';
import { log } from '../utils/errors/logger';
import type { AuthUser } from '../types/common';

interface LoginProps {
  onLogin: (user: AuthUser) => void;  // CHANGED: Removed token parameter (cookies handle auth)
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const { t } = useTranslation();
  const { system } = useSystemPublic();
  const { data: authProviders, isLoading: authProvidersLoading, error: authProvidersError } = usePublicAuthProviders();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | React.ReactNode>('');
  const [loading, setLoading] = useState(false);
  const [versionInfo, setVersionInfo] = useState<{api: string, web: string}>({ api: '...', web: '...' });
  const [adminExists, setAdminExists] = useState(true); // Default: true to hide setup info until we know otherwise
  
  // Get available providers with fallback to local auth only (memoized to prevent re-render loops)
  const availableProviders = React.useMemo(() => {
    return authProviders?.providers || [
      {
        provider_type: 'local',
        is_primary: true,
        login_url: '/api/auth/token',
        display_name: 'Email & Password',
        method: 'form' as const
      }
    ];
  }, [authProviders?.providers]);

  // Load version information and check if admin exists on component mount
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Load version info
        const versions = await VersionService.getVersionInfo();
        setVersionInfo(versions);
        
        // Check if admin user exists
        const hasAdmin = await AuthService.checkAdminExists();
        setAdminExists(hasAdmin);
        
        log.info('Initial login data loaded', { versions, hasAdmin });
      } catch (error) {
        log.error('Failed to load initial data', { error: error instanceof Error ? error.message : String(error) });
      }
    };
    
    // Run only once on mount
    void loadInitialData();
  }, []);

  // Handle Entra initialization when providers are loaded
  useEffect(() => {
    const initializeEntra = async () => {
      if (!authProvidersLoading && authProviders?.providers) {
        const hasEntra = availableProviders.some(p => p.provider_type === 'entra');
        if (hasEntra) {
          try {
            const { default: entraAuthService } = await import('../services/EntraAuthService');
            await entraAuthService.initialize();
            log.info('Entra auth service initialized');
          } catch (error) {
            log.error('Failed to initialize Entra auth service', { error });
          }
        }
      }
    };

    void initializeEntra();
  }, [authProvidersLoading, authProviders, availableProviders]);

  // Debug logging (only on initial load, not on every change)
  React.useEffect(() => {
    if (!authProvidersLoading) {
      log.debug('Auth providers loaded', { 
        authProviders, 
        authProvidersLoading, 
        authProvidersError: authProvidersError?.message || 'No error',
        availableProviders: availableProviders.map(p => p.provider_type),
        providersCount: availableProviders.length
      });
    }
  }, [authProvidersLoading]); // Only trigger when loading state changes

  // Get local auth provider (form-based login)
  const localProvider = availableProviders.find(p => p.provider_type === 'local');
  const redirectProviders = availableProviders.filter(p => p.method === 'redirect');

  // Handle redirect-based auth (like Entra)
  const handleRedirectLogin = async (provider: {
    provider_type: string;
    login_url: string;
  }) => {
    log.user.action('redirect_login_attempt', 'Login', { provider: provider.provider_type });
    
    // For Entra, use MSAL instead of backend redirect
    if (provider.provider_type === 'entra') {
      try {
        setLoading(true);
        const { default: entraAuthService } = await import('../services/EntraAuthService');
        await entraAuthService.initialize();
        await entraAuthService.loginRedirect();
        // MSAL will handle the redirect
      } catch (error) {
        log.error('Entra login failed', { error });
        setError(t('login.error.entraFailed', 'Microsoft Entra login failed. Please try again.'));
        setLoading(false);
      }
    } else {
      // Other providers use backend redirect
      window.location.href = provider.login_url;
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!username || !password) {
      setError(t('login.validation.requiredFields', 'Bitte geben Sie Benutzernamen und Passwort ein'));
      return;
    }
    
    setError('');
    setLoading(true);
    
    try {
      // CHANGED: AuthService.login() now handles cookie-based auth
      // It returns void and internally calls /auth/token + /users/who-am-i
      await AuthService.login(username, password);

      // Get user data that was cached by AuthService.login()
      const userData = AuthService.getUser();

      if (userData && onLogin) {
        // Pass complete user data including permissions
        onLogin(userData);
      } else {
        setError(t('login.validation.invalidResponse', 'Ungültige Antwort vom Server'));
      }
    } catch (error: unknown) {
      log.error('Login error', { error: error instanceof Error ? error.message : String(error), username });
      
      // Spezifische Fehlermeldungen basierend auf der Serverantwort
      const errorWithResponse = error as { response?: { data?: { detail?: string } } };
      if (typeof errorWithResponse.response?.data?.detail === 'string') {
        const errorDetail = errorWithResponse.response.data.detail;
        
        if (errorDetail.includes('email')) {
          setError(
            <>
              {t('login.error.emailNotVerified', 'Bitte verifizieren Sie zuerst Ihre E-Mail-Adresse.')}
              <Box sx={{ mt: 1 }}>
                <Button 
                  component={RouterLink} 
                  to="/resend-verification" 
                  size="small" 
                  color="primary"
                  variant="text"
                >
                  {t('login.resendVerification', 'Verifizierungs-E-Mail erneut senden')}
                </Button>
              </Box>
            </>
          );
        } else if (errorDetail.includes('admin')) {
          setError(t('login.error.awaitingApproval', 'Ihr Konto wurde verifiziert, muss aber noch von einem Administrator freigeschaltet werden.'));
        } else if (errorDetail.includes('inactive')) {
          setError(t('login.error.accountInactive', 'Ihr Konto ist nicht aktiv.'));
        } else {
          setError(errorDetail || t('error.authFailed', 'Anmeldung fehlgeschlagen'));
        }
      } else {
        setError(t('error.authFailed', 'Anmeldung fehlgeschlagen. Bitte überprüfen Sie Ihre Anmeldedaten.'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Get system branding with fallbacks
  const appTitle = system?.name || 'Smarty';
  const appDescription = system?.description || 'Smart Document Management System';
  const logoUrl = system?.branding?.logo_url || '/logo-de.png';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            linear-gradient(135deg, 
              rgba(0, 48, 100, 0.02) 0%, 
              rgba(255, 255, 255, 0.65) 30%,
              rgba(255, 255, 255, 0.65) 70%,
              rgba(225, 158, 25, 0.02) 100%
            ),
            radial-gradient(ellipse at top left, rgba(0, 48, 100, 0.01) 0%, transparent 50%),
            radial-gradient(ellipse at bottom right, rgba(225, 158, 25, 0.01) 0%, transparent 50%)
          `,
          zIndex: 2,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            rgba(255, 255, 255, 0.1),
            repeating-linear-gradient(
              45deg,
              transparent,
              transparent 120px,
              rgba(0, 48, 100, 0.008) 120px,
              rgba(0, 48, 100, 0.008) 122px
            )
          `,
          zIndex: -1,
        }
      }}
    >
      {/* Background Video */}
      <Box
        component="video"
        autoPlay
        muted
        loop
        playsInline
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 1,
          opacity: 0.7,
        }}
      >
        <source src="/Titelvideo_CPRO_Homepage_.mp4" type="video/mp4" />
        {/* Fallback falls Video nicht lädt */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: `
              url('/cpro-companion-single-transparent.png'),
              url('/abstract.png')
            `,
            backgroundSize: '400px, cover',
            backgroundRepeat: 'no-repeat, no-repeat',
            backgroundPosition: 'top 100px left 80px, center',
          }}
        />
      </Box>

      {/* Login form container */}
      <Container component="main" maxWidth="sm" sx={{ position: 'relative', zIndex: 3 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
        <Paper 
          elevation={0}
          sx={{ 
            p: 4, 
            width: '100%',
            background: 'rgba(255, 255, 255, 0.25)',
            backdropFilter: 'blur(15px) saturate(180%)',
            WebkitBackdropFilter: 'blur(15px) saturate(180%)',
            borderRadius: 4,
            border: '1px solid rgba(255, 255, 255, 0.25)',
            boxShadow: '0 20px 40px rgba(0, 48, 100, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.3)',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: 'linear-gradient(90deg, #003064 0%, #E19E19 100%)',
            }
          }}
        >
          {/* System Logo */}
          <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
            <Box
              component="img"
              src={logoUrl}
              alt={`${appTitle} Logo`}
              sx={{
                maxWidth: '80%',
                height: 'auto',
                maxHeight: '60px',
              }}
            />
          </Box>
          
          <Box sx={{
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            px: 2
          }}>
            <Box sx={{
              flex: '0 0 33.333%',
              display: 'flex',
              justifyContent: 'flex-end',
              pr: 2
            }}>
              <Box
                component="img"
                src={system?.branding?.login_icon_url || "/smarty.gif"}
                alt={appTitle}
                sx={{
                  height: '80px',
                  width: 'auto',
                  opacity: 0,
                  animation: 'fadeInGif 0.5s ease-in forwards',
                  animationDelay: '1s',
                  '@keyframes fadeInGif': {
                    '0%': {
                      opacity: 0,
                    },
                    '100%': {
                      opacity: 1,
                    }
                  }
                }}
              />
            </Box>
            <Box sx={{
              flex: '0 0 66.666%',
              textAlign: 'left',
              pl: 2
            }}>
              <Typography
                component="h1"
                variant="h4"
                sx={{
                  fontWeight: 600,
                  mb: 0.5,
                  color: 'rgba(0, 48, 100, 0.8)'
                }}
              >
                {appTitle}
              </Typography>
              <Typography
                variant="subtitle1"
                sx={{
                  color: 'rgba(0, 48, 100, 0.8)',
                  opacity: 0.9
                }}
              >
                {appDescription}
              </Typography>
            </Box>
          </Box>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Auth provider error handling */}
          {authProvidersError && !authProvidersLoading && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t('login.error.providersLoadFailed', 'Fehler beim Laden der Anmeldemethoden. Standard-Anmeldung wird verwendet.')}
            </Alert>
          )}

          {/* Redirect-based auth providers (Entra, SAML, etc.) */}
          {redirectProviders.length > 0 && (
            <Box sx={{ mb: 3 }}>
              {redirectProviders.map((provider) => (
                <Button
                  key={provider.provider_type}
                  fullWidth
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    void handleRedirectLogin(provider);
                  }}
                  startIcon={provider.provider_type === 'entra' ? <Microsoft /> : undefined}
                  sx={{ 
                    mb: 1.5,
                    py: 1.5,
                    background: 'rgba(255, 255, 255, 0.6)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(0, 48, 100, 0.3)',
                    color: '#003064',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.8)',
                      border: '1px solid rgba(0, 48, 100, 0.4)',
                      transform: 'translateY(-1px)',
                    }
                  }}
                >
                  {provider.display_name}
                </Button>
              ))}
              {localProvider && (
                <Divider sx={{ my: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('login.or', 'ODER')}
                  </Typography>
                </Divider>
              )}
            </Box>
          )}
          
          {/* Local auth form (only show if local auth is enabled) */}
          {localProvider && (
            <Box component="form" onSubmit={(e) => { void handleSubmit(e); }} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label={t('login.username')}
              name="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  background: 'rgba(255, 255, 255, 0.6)',
                  backdropFilter: 'blur(8px)',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.7)',
                  },
                  '&.Mui-focused': {
                    background: 'rgba(255, 255, 255, 0.8)',
                  }
                }
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label={t('login.password')}
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  background: 'rgba(255, 255, 255, 0.6)',
                  backdropFilter: 'blur(8px)',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.7)',
                  },
                  '&.Mui-focused': {
                    background: 'rgba(255, 255, 255, 0.8)',
                  }
                }
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ 
                mt: 3, 
                mb: 2, 
                py: 1.5,
                backgroundColor: '#003064',
                boxShadow: '0 8px 32px rgba(0, 48, 100, 0.2)',
                '&:hover': {
                  backgroundColor: '#002a56',
                  boxShadow: '0 12px 40px rgba(0, 48, 100, 0.3)',
                  transform: 'translateY(-2px)',
                }
              }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : t('login.signIn')}
            </Button>
            </Box>
          )}
          {/* End local auth form */}
          
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            {/* Only show registration for local auth */}
            {localProvider && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('login.noAccount', 'Noch kein Konto?')}
                </Typography>
                <Button
                  component={RouterLink}
                  to="/register"
                  variant="outlined"
                  fullWidth
                  sx={{ 
                    mb: 2,
                    background: 'rgba(255, 255, 255, 0.6)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(0, 48, 100, 0.3)',
                    color: '#003064',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.8)',
                      border: '1px solid rgba(0, 48, 100, 0.4)',
                      transform: 'translateY(-1px)',
                    }
                  }}
                >
                  {t('login.register', 'Jetzt registrieren')}
                </Button>
              </>
            )}
            {/* Nur anzeigen, wenn kein Admin existiert */}
            {!adminExists && (
            <>
            <Divider sx={{ my: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {t('login.or', 'ODER')}
              </Typography>
            </Divider>
            
            
              
                <Typography variant="body2" color="text.secondary">
                  {t('login.firstTimeSetup')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('login.defaultCredentials')}
                </Typography>
              </>
            )}
            
            {/* Version information */}
            <Box sx={{ 
              mt: 3, 
              pt: 2,
              borderTop: '1px solid rgba(255, 255, 255, 0.3)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(8px)',
              borderRadius: 2,
              px: 2,
              py: 1,
            }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  fontSize: '0.7rem', 
                  mb: 0.5,
                  color: 'rgba(0, 48, 100, 0.7)',
                  fontWeight: 500
                }}
              >
                API v{versionInfo.api} | Web v{versionInfo.web}
              </Typography>
            </Box>
          </Box>
        </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default Login;
